"use client";

import {
  type FocusEvent,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
  useRef,
} from "react";

import cn from "classnames";

import { stepIndex } from "./adtSocialProof.utils";
import { AdtSocialProofControls } from "./adtSocialProofControls.component";

const SWIPE_DISTANCE = 48;

export function AdtSocialProofCarousel({
  count,
  index,
  onSelect,
  previousLabel,
  nextLabel,
  itemLabel,
  groupLabel,
  labelledBy,
  pause,
  placement,
  testId,
  className,
  onPointerEnter,
  onPointerLeave,
  onFocusCapture,
  onBlurCapture,
  children,
}: AdtSocialProofCarouselProps) {
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (count < 2) return;
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const next = stepIndex(index, count, event.key === "ArrowRight");
    onSelect(next);
    const dot = event.currentTarget.querySelector<HTMLButtonElement>(
      `[data-slide-index="${next}"]`,
    );
    dot?.focus();
  };

  const drag = useRef<{ x: number; y: number } | null>(null);

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (count < 2 || event.pointerType === "mouse") return;
    const target = event.target;
    if (
      !(target instanceof Element) ||
      target.closest("button, a, input, textarea")
    ) {
      return;
    }
    drag.current = { x: event.clientX, y: event.clientY };
    try {
      event.currentTarget.setPointerCapture?.(event.pointerId);
    } catch {
      // Synthetic events and already-ended pointers have nothing to capture.
    }
  };

  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    const start = drag.current;
    drag.current = null;
    if (!start || count < 2) return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (Math.abs(dx) < SWIPE_DISTANCE || Math.abs(dx) < Math.abs(dy)) return;
    onSelect(stepIndex(index, count, dx < 0));
  };

  const showControls = count > 1 || Boolean(pause);
  const controls = showControls ? (
    <AdtSocialProofControls
      count={count}
      index={index}
      onSelect={onSelect}
      previousLabel={previousLabel}
      nextLabel={nextLabel}
      itemLabel={itemLabel}
      groupLabel={groupLabel}
      labelledBy={labelledBy}
      pause={pause}
      tone={placement === "overlay" ? "media" : "surface"}
      spacing={placement === "footer" ? "relaxed" : "compact"}
    />
  ) : null;

  return (
    <div
      className={cn("relative min-w-0 touch-pan-y", className)}
      data-testid={testId}
      onKeyDown={onKeyDown}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerCancel={() => {
        drag.current = null;
      }}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onFocusCapture={onFocusCapture}
      onBlurCapture={onBlurCapture}>
      {children}
      {controls && placement === "overlay" ? (
        <div className="absolute inset-x-0 bottom-4 z-30 flex justify-center px-3">
          {controls}
        </div>
      ) : null}
      {controls && placement === "overlay-desktop" ? (
        <div className="flex w-full justify-center px-4 py-4 sm:px-5 lg:absolute lg:inset-x-0 lg:bottom-8 lg:z-30 lg:px-5 lg:py-0">
          {controls}
        </div>
      ) : null}
      {controls && placement === "footer" ? (
        <div className="mt-auto flex justify-start pt-8">{controls}</div>
      ) : null}
    </div>
  );
}

type AdtSocialProofCarouselProps = {
  count: number;
  index: number;
  onSelect: (next: number) => void;
  previousLabel: string;
  nextLabel: string;
  itemLabel: string;
  groupLabel?: string;
  labelledBy?: string;
  pause?: {
    paused: boolean;
    pauseLabel: string;
    resumeLabel: string;
    onToggle: () => void;
  };
  placement: "overlay" | "overlay-desktop" | "footer";
  testId?: string;
  className?: string;
  onPointerEnter?: (event: PointerEvent<HTMLDivElement>) => void;
  onPointerLeave?: (event: PointerEvent<HTMLDivElement>) => void;
  onFocusCapture?: (event: FocusEvent<HTMLDivElement>) => void;
  onBlurCapture?: (event: FocusEvent<HTMLDivElement>) => void;
  children: ReactNode;
};
